import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, MagnifyingGlass, Question as QuestionIcon } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";

const EMPTY = { question: "", options: ["", "", "", ""], correct_index: 0, tags: "" };

export default function BanqueQuestions() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/question-bank").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const tags = useMemo(() => {
    const s = new Set();
    items.forEach((i) => (i.tags || []).forEach((t) => s.add(t)));
    return [...s].sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (tagFilter !== "all" && !(i.tags || []).includes(tagFilter)) return false;
      if (q && !i.question.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, tagFilter]);

  const resetForm = () => { setForm(EMPTY); setEditId(null); };
  const startEdit = (i) => { setForm({ question: i.question, options: [...i.options], correct_index: i.correct_index, tags: (i.tags || []).join(", ") }); setEditId(i.id); setOpen(true); };

  const save = async () => {
    if (!form.question.trim() || form.options.some((o) => !o.trim())) return toast.error("Renseignez la question et les 4 options.");
    const payload = {
      question: form.question, options: form.options, correct_index: form.correct_index,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editId) { await api.put(`/question-bank/${editId}`, payload); toast.success("Question modifiée"); }
      else { await api.post("/question-bank", payload); toast.success("Question ajoutée à la banque"); }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette question de la banque ?")) return;
    try { await api.delete(`/question-bank/${id}`); toast.success("Question supprimée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const canManage = (i) => user.role === "admin" || i.created_by === user.id;

  return (
    <div className="space-y-6" data-testid="question-bank-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Évaluations</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Banque de questions</h1>
          <p className="text-sm text-muted-foreground">Questions réutilisables entre quiz, partagées entre professeurs, organisées par thème.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-bank-question-btn"><Plus size={18} className="mr-2" /> Nouvelle question</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Modifier la question" : "Nouvelle question"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Question</Label><Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="bank-question-text" /></div>
              {form.options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="radio" checked={form.correct_index === i} onChange={() => setForm({ ...form, correct_index: i })} data-testid={`bank-correct-${i}`} />
                  <Input value={o} onChange={(e) => { const n = [...form.options]; n[i] = e.target.value; setForm({ ...form, options: n }); }} placeholder={`Option ${i + 1}`} data-testid={`bank-option-${i}`} />
                </div>
              ))}
              <div>
                <Label>Thèmes (séparés par des virgules)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Stratégie, SWOT, Porter..." data-testid="bank-tags" />
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-bank-question-btn">{editId ? "Enregistrer" : "Ajouter"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher une question..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="bank-search" />
        </div>
        {tags.length > 0 && (
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-48" data-testid="bank-tag-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les thèmes</SelectItem>
              {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={QuestionIcon} title="Aucune question" description="Ajoutez des questions réutilisables pour vos futurs quiz." actionLabel="Nouvelle question" onAction={() => setOpen(true)} />
      ) : (
        <div className="space-y-3">
          {filtered.map((i) => (
            <div key={i.id} className="rounded-xl border border-border bg-card p-4" data-testid={`bank-item-${i.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium">{i.question}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Bonne réponse : {i.options[i.correct_index]} · Ajoutée par {i.created_by_name}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(i.tags || []).map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
                  </div>
                </div>
                {canManage(i) && (
                  <div className="flex shrink-0 gap-1">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(i)} data-testid={`edit-bank-${i.id}`}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(i.id)} data-testid={`del-bank-${i.id}`}><Trash size={16} className="text-accent" /></Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
